import React, { useRef } from 'react';
import { FormattedMessage } from 'react-intl';
import logoTiah from '../../../assets/external/logo-traderev-tiah.svg';
import Icon from '../../../_shared/components/Icon';
import PoweredByTr from '../../../_shared/components/PoweredBy';
import mixpanelConfig from '../../../_shared/constants/mixpanelConfig';
import {
  INTERNAL_LINKS,
  RetailPlatforms
} from '../../../_shared/constants/retail';
import useScrollSticky from '../../../_shared/hooks/useScrollSticky';
import convertToCurrency from '../../../_shared/utils/convertToCurrency';
import { isMobileDevice } from '../../../_shared/utils/userAgent';
import ROUTES from '../../../components/Routes/constants';
import MixpanelService from '../../../services/MixpanelService';

import {
  SideRailContainer,
  Section,
  PriceContainer,
  Price,
  DealershipName,
  AddressContainer,
  AddressLine,
  DirectionsLink,
  Salesperson,
  PhoneNumber,
  Email,
  SeeMore,
  StyledInventoryButton,
  TiahLogo,
  StyledTitle,
  TiahText,
  TiahDealership,
  StyledTiahButton,
  StyledLogo
} from './Styles';

const SideRail = ({
  tiahToken,
  dealer,
  metadata,
  guid,
  tradeDetails,
  dealerLogoSrc,
  isOmp
}) => {
  const mixpanel = MixpanelService.get();
  const siderailRef = useRef(null);
  useScrollSticky(siderailRef);

  const openTiahLink = () => {
    mixpanel.track(mixpanelConfig.names.tiahLinkedOpened, {
      retail_guid: guid,
      year: tradeDetails.year,
      make: tradeDetails.make,
      model: tradeDetails.model,
      vin: tradeDetails.vin,
      trim: tradeDetails.trim,
      platform: isMobileDevice() ? RetailPlatforms.Mobile : RetailPlatforms.Web,
      is_retail_customer:
        JSON.parse(window.localStorage.getItem('auth'))?.user === undefined,
      dealer: dealer?.displayName,
      userToken: tiahToken
    });
    const HostName = isOmp ? ROUTES.OMP_TRADE_IN : ROUTES.TR4_TRADE_IN;
    window.open(
      `${HostName}${tiahToken}${INTERNAL_LINKS.TIAH_REGION}`,
      '_blank'
    );
  };

  const openDealershipLink = () => {
    window.open(`https://${metadata.dealershipLink}`, '_blank');
  };

  const openDealerLocation = () => {
    return `${INTERNAL_LINKS.DIRECTIONS_LINK}${dealer?.street}+${dealer?.city}+${dealer?.province}+${dealer?.zip}`;
  };

  return (
    <SideRailContainer ref={siderailRef}>
      <Section id="side-price-dealership">
        {metadata?.customerPrice && Number(metadata?.customerPrice) && (
          <PriceContainer>
            <Price data-testid="retailVdp-price">
              {convertToCurrency(parseInt(metadata.customerPrice, 10))}
            </Price>
            <FormattedMessage id="retail.customization.price.disclaimer" />
          </PriceContainer>
        )}
        {dealerLogoSrc && (
          <StyledLogo
            data-testid="retailVdp-dealer-logo"
            alt="dealer-logo"
            src={dealerLogoSrc}
          />
        )}
        {dealer?.displayName && (
          <DealershipName data-testid="retailVdp-dealer-name">
            {dealer.displayName}
          </DealershipName>
        )}
      </Section>
      <Section id="side-sales-info">
        <AddressContainer data-testid="retailVdp-dealer-address">
          {dealer?.street && <AddressLine>{dealer.street}</AddressLine>}
          {dealer?.city && dealer?.province && (
            <AddressLine>{`${dealer.city}, ${dealer.province}`}</AddressLine>
          )}
          {dealer?.zip && <AddressLine>{dealer.zip}</AddressLine>}
        </AddressContainer>
        {dealer?.street && dealer?.city && dealer?.province && dealer?.zip && (
          <DirectionsLink
            data-testid="retailVdp-dealership-location"
            href={openDealerLocation()}
            target="_blank"
          >
            <FormattedMessage id="retail.directions.link" />
          </DirectionsLink>
        )}
        {metadata?.salePersonName && (
          <Salesperson data-testid="retailVdp-salesperson-name">
            <FormattedMessage id="retail.salesperson.label" />
            {`: ${metadata.salePersonName}`}
          </Salesperson>
        )}
        {metadata?.salePersonPhone && metadata?.salePersonPhone.length === 10 && (
          <PhoneNumber data-testid="retailVdp-salesperson-number">
            <Icon icon="phone" />
            {metadata.salePersonPhone.slice(0, 3)}.
            {metadata.salePersonPhone.slice(3, 6)}.
            {metadata.salePersonPhone.slice(6, 10)}
          </PhoneNumber>
        )}
        {metadata?.salePersonEmail && (
          <Email data-testid="retailVdp-salesperson-email">
            <Icon icon="email-icon" />
            {metadata.salePersonEmail}
          </Email>
        )}
        {metadata?.dealershipLink && (
          <SeeMore>
            <FormattedMessage id="retail.seeMore.title" />
            <StyledInventoryButton
              data-testid="retailVdp-seeMore-button"
              onClick={openDealershipLink}
            >
              <FormattedMessage id="retail.seeMore.button" />
            </StyledInventoryButton>
          </SeeMore>
        )}
      </Section>
      {tiahToken && dealer?.displayName && (
        <Section id="side-tiah">
          {!isOmp && <TiahLogo src={logoTiah} />}
          <StyledTitle>
            <FormattedMessage id="retail.tiah.title" />
          </StyledTitle>
          <TiahText>
            <FormattedMessage
              id="retail.tiah.text"
              values={{
                dealership: dealer.displayName,
                name: chunks => <TiahDealership>{chunks}</TiahDealership>
              }}
            />
          </TiahText>
          <StyledTiahButton
            data-testid="retailVdp-tiah-button"
            onClick={openTiahLink}
          >
            <FormattedMessage id="retail.tiah.button" />
          </StyledTiahButton>
          {!isOmp && <PoweredByTr />}
        </Section>
      )}
    </SideRailContainer>
  );
};

export default SideRail;
